import { useEffect, useRef, useState } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { fromLonLat } from 'ol/proj';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import 'ol/ol.css';
import { Box, Paper, Typography } from '@material-ui/core';

const HAWAII_CENTER = [-156.3, 20.4];
const DEFAULT_ZOOM = 7;

const MARKER_STYLE = new Style({
    image: new CircleStyle({
        radius: 8,
        fill: new Fill({ color: '#364153' }),
        stroke: new Stroke({ color: '#2c3749', width: 2 })
    })
});

export default function HotelsMapView({ hotels }) {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);

    const [selectedHotel, setSelectedHotel] = useState(null);
    const [popupPixel, setPopupPixel] = useState([0, 0]);

    const hotelsWithCoords = (hotels || []).filter(
        h => h.latitude != null && h.longitude != null
    );
    const hotelsWithout = (hotels || []).filter(
        h => h.latitude == null || h.longitude == null
    );

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) {
            return;
        }

        const markerSource = new VectorSource();

        hotelsWithCoords.forEach(hotel => {
            const feature = new Feature({
                geometry: new Point(
                    fromLonLat([parseFloat(hotel.longitude), parseFloat(hotel.latitude)])
                ),
                hotel
            });
            markerSource.addFeature(feature);
        });

        const markerLayer = new VectorLayer({
            source: markerSource,
            style: MARKER_STYLE
        });

        const map = new Map({
            target: mapRef.current,
            layers: [
                new TileLayer({
                    source: new XYZ({
                        url: 'https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}@2x.png',
                        attributions: '© OpenStreetMap contributors © CARTO'
                    })
                }),
                markerLayer
            ],
            view: new View({
                center: fromLonLat(HAWAII_CENTER),
                zoom: DEFAULT_ZOOM
            })
        });

        map.on('click', evt => {
            const feature = map.forEachFeatureAtPixel(evt.pixel, f => f);
            if (feature) {
                setSelectedHotel(feature.get('hotel'));
                setPopupPixel(evt.pixel);
            } else {
                setSelectedHotel(null);
            }
        });

        map.on('pointermove', evt => {
            const hit = map.hasFeatureAtPixel(evt.pixel);
            map.getTargetElement().style.cursor = hit ? 'pointer' : '';
        });

        mapInstanceRef.current = map;

        return () => {
            map.setTarget(null);
            mapInstanceRef.current = null;
        };
    }, []);

    return (
        <Box style={{ width: '100%' }}>
            <div ref={mapContainerRef} style={{ position: 'relative', width: '100%' }}>
                <div
                    ref={mapRef}
                    style={{
                        width: '100%',
                        height: 520,
                        borderRadius: 8,
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.1)',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                    }}
                />
                {hotelsWithCoords.length === 0 && (
                    <Box
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            pointerEvents: 'none'
                        }}
                    >
                        <Typography
                            variant="body2"
                            style={{
                                background: 'rgba(255,255,255,0.85)',
                                padding: '8px 16px',
                                borderRadius: 4,
                                color: '#555'
                            }}
                        >
                            No hotels with coordinates to display
                        </Typography>
                    </Box>
                )}
                {selectedHotel && (
                    <Paper
                        elevation={4}
                        style={{
                            position: 'absolute',
                            left: popupPixel[0],
                            top: popupPixel[1] - 14,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 1000,
                            maxWidth: 260,
                            minWidth: 180,
                            pointerEvents: 'auto',
                            borderRadius: 8,
                            overflow: 'hidden'
                        }}
                    >
                        {/* header strip */}
                        <Box
                            style={{
                                background: '#364153',
                                padding: '8px 12px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 8
                            }}
                        >
                            <Typography
                                style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    lineHeight: 1.3,
                                    color: '#fff',
                                    flex: 1
                                }}
                            >
                                {selectedHotel.name}
                            </Typography>
                            <span
                                onClick={() => setSelectedHotel(null)}
                                style={{
                                    cursor: 'pointer',
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: 18,
                                    lineHeight: 1,
                                    flexShrink: 0
                                }}
                            >
                                ×
                            </span>
                        </Box>
                        {/* body */}
                        <Box style={{ padding: '8px 12px', background: '#fff' }}>
                            {selectedHotel.island?.name && (
                                <Typography
                                    variant="caption"
                                    style={{ display: 'block', color: '#717182', fontWeight: 500 }}
                                >
                                    {selectedHotel.island.name}
                                </Typography>
                            )}
                            {selectedHotel.hotel_type && (
                                <Typography
                                    variant="caption"
                                    style={{ display: 'block', color: '#717182' }}
                                >
                                    {selectedHotel.hotel_type}
                                </Typography>
                            )}
                            {selectedHotel.address && (
                                <Typography
                                    variant="caption"
                                    style={{ display: 'block', color: '#9ca3af', marginTop: 4 }}
                                >
                                    {selectedHotel.address}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                )}
            </div>
            {hotelsWithout.length > 0 && (
                <Typography
                    variant="caption"
                    style={{ display: 'block', marginTop: 6, color: '#999' }}
                >
                    {hotelsWithout.length} hotel(s) have no coordinates and are not shown on the map.
                </Typography>
            )}
        </Box>
    );
}
